import * as React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper/types';
import 'swiper/css';

import { IconSize } from 'shared/entities/components/Icon';
import { useTypedTranslation } from 'shared/entities/localization';
import type { NewsContentNode } from 'shared/entities/newsContent';
import FadeScroll from 'shared/newComponents/FadeScroll';
import NewsContentBody from 'shared/newComponents/NewsContentBody';
import SocialNetworks from 'shared/newComponents/SocialNetworks';
import ArrowIcon from 'shared/newComponents/icons/ArrowIcon';

import NewsModalCarouselNavigation from './NewsModalCarouselNavigation';
import styles from './NewsModalCarousel.modules.scss';

const MAX_VISIBLE_BULLETS = 9;

type Props<T extends NewsCarouselItem> = {
  activeItemId?: string;
  isCompactNavigation: boolean;
  items: T[];
  onActiveNewsChange: (news: T) => void;
  socialNetworksTitle?: string;
};

export type NewsCarouselItem = {
  body: NewsContentNode;
  id: string;
  isTestActive?: boolean;
  title: string;
};

const NewsModalCarousel = <T extends NewsCarouselItem>({
  activeItemId,
  isCompactNavigation,
  items,
  onActiveNewsChange,
  socialNetworksTitle
}: Props<T>): React.ReactElement => {
  const { t } = useTypedTranslation('app');
  const initialIndex = activeItemId
    ? Math.max(
        items.findIndex((item) => item.id === activeItemId),
        0
      )
    : 0;
  const [activeIndex, setActiveIndex] = React.useState(initialIndex);
  const [swiper, setSwiper] = React.useState<SwiperInstance | null>(null);
  const hasNavigation = items.length > 1;
  // Активный элемент остается по центру, пока не достигнут край списка.
  const paginationStart = Math.min(
    Math.max(activeIndex - Math.floor(MAX_VISIBLE_BULLETS / 2), 0),
    Math.max(items.length - MAX_VISIBLE_BULLETS, 0)
  );
  const paginationItems = items.slice(
    paginationStart,
    paginationStart + MAX_VISIBLE_BULLETS
  );

  const slidePrev = React.useCallback(() => {
    swiper?.slidePrev();
  }, [swiper]);

  const slideNext = React.useCallback(() => {
    swiper?.slideNext();
  }, [swiper]);

  const handleSlideChange = React.useCallback(
    (instance: SwiperInstance) => {
      const nextActiveIndex = instance.activeIndex;

      setActiveIndex(nextActiveIndex);
      onActiveNewsChange(items[nextActiveIndex] || items[0]);
    },
    [items, onActiveNewsChange]
  );

  React.useEffect(() => {
    const nextActiveIndex = activeItemId
      ? items.findIndex((item) => item.id === activeItemId)
      : 0;

    if (
      nextActiveIndex < 0 ||
      !swiper ||
      (nextActiveIndex === activeIndex &&
        swiper.activeIndex === nextActiveIndex)
    ) {
      return;
    }

    setActiveIndex(nextActiveIndex);
    swiper.slideTo(nextActiveIndex, 0);
  }, [activeIndex, activeItemId, items, swiper]);

  React.useEffect(() => {
    if (activeIndex < items.length) {
      return;
    }

    setActiveIndex(0);
    onActiveNewsChange(items[0]);
  }, [activeIndex, items, onActiveNewsChange]);

  return (
    <div styleName="news-modal-carousel">
      {hasNavigation && (
        <NewsModalCarouselNavigation
          activeIndex={activeIndex}
          items={paginationItems}
          nextDisabled={!swiper || swiper.isEnd}
          onNext={slideNext}
          onPrevious={slidePrev}
          onSelect={(index) => swiper?.slideTo(index)}
          paginationStart={paginationStart}
          previousDisabled={!swiper || swiper.isBeginning}
          withArrows={isCompactNavigation}
        />
      )}
      <Swiper
        styleName="news-modal-carousel__swiper"
        autoHeight
        initialSlide={initialIndex}
        slidesPerView={1}
        loop={false}
        simulateTouch={false}
        touchStartPreventDefault={false}
        onInit={setSwiper}
        onSlideChange={handleSlideChange}
      >
        {items.map((item) => (
          <SwiperSlide key={item.id}>
            <FadeScroll
              className={styles['news-modal-carousel__slide']}
              contentClassname={styles['news-modal-carousel__slide-content']}
            >
              <NewsContentBody
                styleName="news-modal-carousel__content"
                content={{
                  body: item.body,
                  badgeLabel: item.isTestActive
                    ? t('news.testBadge')
                    : undefined,
                  id: item.id,
                  title: item.title
                }}
              />
              <div styleName="news-modal-carousel__social-networks">
                <SocialNetworks
                  title={socialNetworksTitle || t('news.socialNetworks')}
                />
              </div>
            </FadeScroll>
          </SwiperSlide>
        ))}
      </Swiper>
      {!isCompactNavigation && hasNavigation && (
        <div styleName="news-modal-carousel__side-controls">
          <button
            aria-label={t('news.navigation.previous')}
            disabled={!swiper || swiper.isBeginning}
            styleName="news-modal-carousel__side-arrow news-modal-carousel__side-arrow_prev"
            type="button"
            onClick={slidePrev}
          >
            <ArrowIcon direction="left" iconSize={IconSize.XL} />
          </button>
          <button
            aria-label={t('news.navigation.next')}
            disabled={!swiper || swiper.isEnd}
            styleName="news-modal-carousel__side-arrow news-modal-carousel__side-arrow_next"
            type="button"
            onClick={slideNext}
          >
            <ArrowIcon direction="right" iconSize={IconSize.XL} />
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsModalCarousel;
